import { FC, useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import { BACKEND_URL } from '../config'
import { fetchAllData, fetchWithAuthMethod } from '../utils';


export const EditDeviceTypes: FC = () => {
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [deviceTypes, setDeviceTypes] = useState<any[]>([]);
  const [deviceTypeName, setDeviceTypeName] = useState('');
  const [deviceTypeDescription, setDeviceTypeDescription] = useState('');
  const [deviceTypeMaxChannels, setDeviceTypeMaxChannels] = useState('');
  const [selectedDeviceTypeId, setSelectedDeviceTypeId] = useState<string | null>(null); 
  const [isLoading, setIsLoading] = useState(false);
  const [deletingDeviceTypeId, setDeletingDeviceTypeId] = useState(null);
  
  useEffect(() => {
    const fetchData = async () => {
      const deviceTypesResponse = await fetchAllData(`${BACKEND_URL}/device_types`);
      setDeviceTypes(deviceTypesResponse);
    };
    
    fetchData().catch(console.error);
  }, []);

  const handleEditClick = async (row: any) => {
    setSelectedDeviceTypeId(row.id);
    setDeviceTypeName(row.name);
    setDeviceTypeDescription(row.description);
    setDeviceTypeMaxChannels(row.max_channels);
    setOpenEditDialog(true); // 編集ダイアログを開く

    setTimeout(() => {
      document.getElementById('device-type-name-input')?.focus(); // IDを使ってフォーカス
    }, 0);
  };

  // チャネル数入力チェック
  const validateMaxChannels = (): boolean => {
    const maxChannels = Number(deviceTypeMaxChannels);
    if (isNaN(maxChannels) || maxChannels < 1 || maxChannels > 4) {
      alert("チャネル数は 1 以上 4 以下で入力してください");
      return false;
    }
  
    return true;
  };
  
  const handleEditDeviceType = async () => {
    // 入力チェック
    if (!validateMaxChannels()) return;

    // 確認メッセージを表示
    const confirmed = window.confirm('更新してもよろしいですか？');
    if (!confirmed) return; // ユーザーがキャンセルした場合は処理しない

    setIsLoading(true); // ローディング開始

    const response = await fetchWithAuthMethod(`${BACKEND_URL}/device_types/${selectedDeviceTypeId}`, `PUT`, {
      name: deviceTypeName,
      description: deviceTypeDescription,
      max_channels: deviceTypeMaxChannels
    });

    if (response.ok) {
      // 端末種別更新が成功した場合は端末種別リストを更新
      const updatedDeviceType = await response.json();
      // 端末種別更新
      setDeviceTypes(prevDeviceTypes => {
        const newDeviceTypes = [...prevDeviceTypes];
        const index = newDeviceTypes.findIndex(device_type => device_type.id === selectedDeviceTypeId);    
        if (0 <= index) {
          // 指定端末種別のデータのみ更新
          newDeviceTypes[index] = {
            ...updatedDeviceType
          };
        }
        return newDeviceTypes;
      });

      handleCloseDialog('edit'); // 編集ダイアログを閉じる
    } else {
      const data = await response.json();
      if (data.detail.code === "DEVICE_TYPE_NAME_EXISTS") {
        alert("指定された端末種別名称は既に登録されています。");
      } else if (data.detail.code === "CHANNEL_EXISTS") {
        alert(`${Number(deviceTypeMaxChannels)+1}チャネル以降に割り当てられているセンサ種別があるため、指定のチャネル数には変更できません。`);
      }

      console.error('Failed to update device_type');
    }

    setIsLoading(false); // ローディング終了
  };

  const handleCloseDialog = (dialogType: 'add' | 'edit', event?: React.SyntheticEvent<Element, Event> | {}, reason?: string) => {
    // 背景をクリックされるとダイアログの各値がクリアされてしまう対策
    if (reason && reason === 'backdropClick') {
      return;
    }
    
    setDeviceTypeName('');
    setDeviceTypeDescription('');
    setDeviceTypeMaxChannels('');
	
    if (dialogType === 'add') {
      setOpenAddDialog(false); // 追加ダイアログを閉じる
    } else if (dialogType === 'edit') {
      setOpenEditDialog(false); // 編集ダイアログを閉じる
    }
  };

  const handleDeleteClick = async (row: any) => {
    // 確認メッセージを表示
    const confirmed = window.confirm('削除してもよろしいですか？');
    if (!confirmed) return; // ユーザーがキャンセルした場合は処理しない

    setDeletingDeviceTypeId(row.id);
    setIsLoading(true); // ローディング開始
     
    // 端末種別削除
    const response = await fetchWithAuthMethod(`${BACKEND_URL}/device_types/${row.id}`, `DELETE`);
    if (response.ok) {
      // 端末種別リスト更新
      setDeviceTypes(deviceTypes.filter((device_type: any) => device_type.id !== row.id));
    } else {
      const data = await response.json();
      if (data.detail.code === "HAS_LINKED_DEVICE_TYPES") {
        alert("この端末種別が割り当てられている地点データが存在するため、削除できません。");
      }

      console.error('Failed to delete device_type');
    }

    setIsLoading(false); // ローディング終了
    setDeletingDeviceTypeId(null);
  };
  
  const handleAddClick = () => {
    setOpenAddDialog(true);

    setTimeout(() => {
      document.getElementById('device-type-name-input')?.focus(); // IDを使ってフォーカス
    }, 0);
  };

  const handleAddDeviceType = async () => {
    // 入力チェック
    if (!validateMaxChannels()) return;

    // 確認メッセージを表示
    const confirmed = window.confirm('追加してもよろしいですか？');
    if (!confirmed) return; // ユーザーがキャンセルした場合は処理しない

    setIsLoading(true); // ローディング開始

    // 端末種別の追加
    const response = await fetchWithAuthMethod(`${BACKEND_URL}/device_types`, `POST`, {
      name: deviceTypeName,
      description: deviceTypeDescription,
      max_channels: deviceTypeMaxChannels
    });

    if (response.ok) {
      // 新しく作成された端末種別を取得
      const newDeviceType = await response.json();
      setDeviceTypes([...deviceTypes, newDeviceType]); // 端末種別リストに新しい端末種別を追加する
	
      handleCloseDialog('add'); // 追加ダイアログを閉じる
    } else {
      const data = await response.json();
      if (data.detail.code === "DEVICE_TYPE_NAME_EXISTS") {
        alert("指定された端末種別名称は既に登録されています。");
      }

      console.error('Failed to add device_type');
    }

    setIsLoading(false); // ローディング終了
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: '端末種別名称',
      editable: false,
      width: 160,
    },
    {
      field: 'description',
      headerName: '説明',
      editable: false,
      width: 160,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'max_channels',
      headerName: 'チャネル数',
      editable: false,
      width: 160,
      align: 'right',
    },
    {
      field: 'edit',
      headerName: '編集',
      width: 60,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton aria-label="edit" onClick={() => handleEditClick(params.row)}>
          <EditIcon />
        </IconButton>
      ),
    },
    {
      field: 'delete',
      headerName: '削除',
      width: 60,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton aria-label="delete" onClick={() => handleDeleteClick(params.row)}>
            {deletingDeviceTypeId === params.row.id ? (
              <CircularProgress size={24} />
            ) : (
              <DeleteIcon />
            )}
        </IconButton>
      ),
    }
  ];

  return (
    <Box sx={{ height: '80vh', width: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 250px)' }} id="datalist-grid">
        <DataGrid
          rows={deviceTypes}
          columns={columns}
          pageSize={100} // 1ページの表示件数
          rowsPerPageOptions={[100]}
          disableSelectionOnClick
          experimentalFeatures={{ newEditingApi: true }}
          density="compact"
          sx={{ flexGrow: 1, overflowY: 'auto',
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
            "& .Mui-selected": {
              outline: "none",
            },
          }}
        />
      </Box>
      <IconButton aria-label="add" onClick={handleAddClick}>
        <AddIcon />
		<Typography variant="body1">追加</Typography>
      </IconButton>
      <Dialog open={openAddDialog} onClose={(event, reason) => handleCloseDialog('add', event || {}, reason)}>
        <DialogTitle>端末種別の追加</DialogTitle>
        <DialogContent>
          <TextField
            id="device-type-name-input"
            autoFocus
            margin="dense"
            label="端末種別名称"
            fullWidth
            value={deviceTypeName}
            onChange={(e) => setDeviceTypeName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="説明"
            fullWidth
            value={deviceTypeDescription}
            onChange={(e) => setDeviceTypeDescription(e.target.value)}
          />
          <TextField
            margin="dense"
            label="チャネル数"
            fullWidth
            value={deviceTypeMaxChannels}
            onChange={(e) => setDeviceTypeMaxChannels(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleCloseDialog('add')}>キャンセル</Button>
          {isLoading ? (
            <CircularProgress /> // MUIのスピナーを表示
          ) : (
            <Button 
              onClick={handleAddDeviceType}
              disabled={((!deviceTypeName || 0 >= deviceTypeName.length) || 
                (!deviceTypeMaxChannels || 0 >= deviceTypeMaxChannels.length))}
            >
              追加
            </Button>
          )}        
        </DialogActions>
      </Dialog>
      <Dialog open={openEditDialog} onClose={(event, reason) => handleCloseDialog('edit', event || {}, reason)}>
        <DialogTitle>端末種別の編集</DialogTitle>
        <DialogContent>
          <TextField
            id="device-type-name-input"
            autoFocus
            margin="dense"
            label="端末種別名称"
            fullWidth
            value={deviceTypeName}
            onChange={(e) => setDeviceTypeName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="説明"
            fullWidth
            value={deviceTypeDescription}
            onChange={(e) => setDeviceTypeDescription(e.target.value)}
          />
          <TextField
            margin="dense"
            label="チャネル数"
            fullWidth
            value={deviceTypeMaxChannels}
            onChange={(e) => setDeviceTypeMaxChannels(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleCloseDialog('edit')}>キャンセル</Button>
          {isLoading ? (
            <CircularProgress /> // MUIのスピナーを表示
          ) : (
            <Button 
              onClick={handleEditDeviceType}
              disabled={((!deviceTypeName || 0 >= deviceTypeName.length) || 
                (!deviceTypeMaxChannels || 0 >= deviceTypeMaxChannels.length))}
            >
              更新
            </Button>            
          )} 
        </DialogActions>
      </Dialog>
    </Box>
  );
}